import { Graph, GraphAnimateConfig, INode, Matrix, Util, IBBox, Item, IShapeBase } from '@antv/g6';
import { ItemState, VisibilityState, GraphBehavior } from '../constants';
import { getGlobal } from './config';
import { select } from './edit';
import { isBehaviorEnabled, getBehaviors, isBehaviorType } from './helpers';

const ZOOM_DELTA = 0.05;

/**
 * Gets the BBox for the viewport (IE: what we see on screen)
 * Zoom effects the viewport size.  As a result:
 *   - If zoomed IN, viewport width/height < graph width/height
 *   - If zoomed OUT, viewport width/height > graph width/height
 */
export const getViewBBox = (graph: Graph): IBBox => {
  // Transform viewport coordinates into canvas coordinates.
  const { x: minX, y: minY } = graph.getPointByCanvas(0, 0);
  const { x: maxX, y: maxY } = graph.getPointByCanvas(graph.getWidth(), graph.getHeight());
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    minX,
    minY,
    maxX,
    maxY,
    x: minX,
    y: minY,
    width,
    height,
    // Functionality taken from G6 > ItemBase > calculateBBox
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

/**
 * Is an item/shape within the current viewport?
 * NOTE: By design, this only measures coordinates.  If the item is hidden, this
 * will still return true.  This should not be changed.  If you need to check
 * both, this should be done in a seperate function.
 */
export const isInViewport = (graph: Graph, item: Item | IShapeBase) => {
  if (!item || item.destroyed) return false;
  return Util.intersectBBox(getViewBBox(graph), item.getBBox());
};

/**
 * Gets the currently set zoom
 */
export const getZoom = (graph: Graph): number => graph?.getZoom();

/**
 * Gets the zoom percentage of the current file
 */
export const getZoomPercentage = (graph: Graph): number => Math.round(getZoom(graph) * 100);

/**
 * Assuming that the graph has zoom/pan permissions, should the canvas be able
 * to be zoomed or panned?
 */
export const isCanvasMovable = (graph?: Graph | null) => {
  if (!graph) return false;
  // Stops zoom when overlay is visible to avoid boundaries going haywire
  if (graph?.get('isOverlayVisible') === true) return false;

  const { contextMenu, inlineEditInput } = getGlobal(graph).plugin;

  return (
    contextMenu.state === VisibilityState.Hide && inlineEditInput.state === VisibilityState.Hide
  );
};

/**
 * Does a zoom behavior exist?
 */
export const isZoomBehaviorEnabled = (graph: Graph) => {
  if (isBehaviorEnabled(graph, GraphBehavior.ZoomCanvas)) return true;

  const behaviors = getBehaviors(graph);
  const scrollBehavior = behaviors?.find((b) => isBehaviorType(b, GraphBehavior.ScrollCanvas));

  if (!scrollBehavior) return false;
  // If the behavior name is a string, then they're using the defaults for
  // scroll-canvas, which includes zoom.
  if (typeof scrollBehavior === 'string' || !Object.keys(scrollBehavior).includes('zoomKey')) {
    return true;
  }

  return typeof scrollBehavior?.zoomKey === 'string';
};

/**
 * Are you able to zoom?
 *
 * There are three situations in which this will be false:
 * 1. The graph has not rendered
 * 2. You are trying to zoom out, but are already at min zoom
 * 3. You are trying to zoom in, but are already at max zoom
 */
export const canZoom = (graph: Graph, dir?: 'out' | 'in'): boolean => {
  if (!graph) return false;
  if (!isZoomBehaviorEnabled(graph)) return false;
  if (!isCanvasMovable(graph)) return false;
  if (dir !== 'out' && dir !== 'in') return true;

  const isZoomOut = dir === 'out';
  const ratio = isZoomOut ? 1 - ZOOM_DELTA : 1 + ZOOM_DELTA;
  // adjust to 2 decimal points
  const adjustedZoom = Math.round(getZoom(graph) * ratio * 100) / 100;

  return isZoomOut ? adjustedZoom > graph.get('minZoom') : adjustedZoom < graph.get('maxZoom');
};

/**
 * Are you able to zoom out?
 */
export const canZoomOut = (graph: Graph): boolean => canZoom(graph, 'out');

/**
 * Are you able to zoom in?
 */
export const canZoomIn = (graph: Graph): boolean => canZoom(graph, 'in');

/**
 * Zoom to x ratio of original graph.
 */
export const zoomTo = (graph: Graph, n: number) => graph?.zoomTo(n);

/**
 * Navigate to the center of the graph
 */
export const center = (graph: Graph) => graph?.fitCenter();

/**
 * Fit everything in the graph on the screen
 */
export const fitScreen = (graph: Graph) => graph?.fitView();

/**
 * Focus on a specific node
 */
export const focus = (
  graph: Graph,
  id: string,
  animate?: boolean,
  animateCfg?: GraphAnimateConfig,
) => graph?.focusItem(id, animate, animateCfg);

export enum FocusPosition {
  UpperLeft = 'UL',
  UpperRight = 'UR',
  BottomLeft = 'BL',
  BottomRight = 'BR',
  Center = 'center',
}

/**
 * Gets the view points of a specific position
 */
export const getViewPoints = (graph: Graph, position: FocusPosition = FocusPosition.UpperLeft) => {
  const viewController = graph.get('viewController');
  const [top, right, bottom, left] = viewController.getFormatPadding();

  switch (position) {
    case FocusPosition.UpperRight:
      return { x: graph.get('width') - right, y: top };
    case FocusPosition.BottomLeft:
      return { x: left, y: graph.get('height') - bottom };
    case FocusPosition.BottomRight:
      return { x: graph.get('width') - right, y: graph.get('height') - bottom };
    case FocusPosition.Center:
      return {
        x: (graph.get('width') - right - left) / 2 + left,
        y: (graph.get('height') - top - bottom) / 2 + top,
      };
    default:
      return { x: left, y: top };
  }
};

/**
 * Gets the view points of a node in a specific position
 */
export const getNodeViewPoints = (
  graph: Graph,
  position: FocusPosition = FocusPosition.UpperLeft,
  node: INode,
) => {
  let { x, y } = getViewPoints(graph, position);

  if (position === FocusPosition.UpperLeft) return { x, y };

  const { width, height } = node.getBBox();

  if ([FocusPosition.UpperRight, FocusPosition.BottomRight].includes(position)) {
    x -= width;
  }

  if ([FocusPosition.BottomLeft, FocusPosition.BottomRight].includes(position)) {
    y -= height;
  }

  if (position === FocusPosition.Center) {
    x -= width / 2;
    y -= height / 2;
  }

  return { x, y };
};

/**
 * Focuses on a node in a specific position
 */
export const focusNode = (
  graph: Graph,
  node: string | INode,
  position: FocusPosition = FocusPosition.UpperLeft,
  animate = false,
  animateCfg?: GraphAnimateConfig,
  translateShift?: { x: number; y: number },
) => {
  if (!node) return;

  const graphNode = typeof node === 'string' ? graph.findById(node) : node;

  if (!graphNode) return;

  const { x = 50, y = 50 } = graphNode.getBBox();
  const viewController = graph.get('viewController');
  const viewPosition = getNodeViewPoints(graph, position, graphNode as INode);
  const modelCenter = viewController.getPointByCanvas(
    viewPosition.x + (translateShift?.x || 0),
    viewPosition.y + (translateShift?.y || 0),
  );
  const viewportMatrix: Matrix = graph.get('group').getMatrix() ?? [1, 0, 0, 0, 1, 0, 0, 0, 1];

  if (animate) {
    const dx = (modelCenter.x - x) * viewportMatrix[0];
    const dy = (modelCenter.y - y) * viewportMatrix[4];
    let lastX = 0;
    let lastY = 0;
    let newX = 0;
    let newY = 0;
    // The animation translates a little at a time until it reaches the target position
    graph.get('canvas').animate(
      (ratio: number) => {
        newX = dx * ratio;
        newY = dy * ratio;
        graph.translate(newX - lastX, newY - lastY);
        lastX = newX;
        lastY = newY;
      },
      { ...graph.get('animateCfg'), ...animateCfg },
    );
  } else {
    graph.translate(
      (modelCenter.x - x) * viewportMatrix[0],
      (modelCenter.y - y) * viewportMatrix[4],
    );
  }
};

/**
 * Focus on a specific node
 */
export const goToNode = (graph: Graph, id: string) => {
  if (!graph || !id) return;

  const node = graph.findById(id);

  if (node) {
    zoomTo(graph, 2);
    focusNode(graph, id, FocusPosition.UpperLeft, true);
    select(graph, node);
    node.toFront();
    graph.priorityState(node, ItemState.Selected);
  }
};
